// People & records stream components (ADR-023): restricted profile details with purpose-bound
// reveals (PB-03-07), configured profile/host fields (OR-07, OR-08), the per-category screening rule
// editor (PB-03-06) and reception's "run the check again" action. The API authorises everything;
// revealed values live only in component state (never the query cache) and are hidden on expiry or
// as soon as the server says the reveal is no longer valid.
import type { ProfileFieldDto, ProfileFieldsResponse, RevealFieldsResponse, RevealHistoryResponse, ScreeningFactDto } from '@passage/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ApiRequestError, api, get } from '../api.ts';
import { useI18n } from '../i18n.tsx';
import { useSession } from '../session.tsx';
import { Dialog, ErrorState, ErrorSummary, Field, Loading } from './ui.tsx';

type FieldValue = string | number | boolean | null;

function FieldInput({ f, id, value, onChange, props }: { f: Pick<ProfileFieldDto, 'type' | 'options' | 'maxLength'>; id: string; value: string; onChange: (v: string) => void; props: Record<string, unknown> }) {
  const { t } = useI18n();
  if (f.type === 'boolean')
    return (
      <select {...props} id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{t('common.none')}</option>
        <option value="true">{t('common.yes')}</option>
        <option value="false">{t('common.no')}</option>
      </select>
    );
  if (f.type === 'select')
    return (
      <select {...props} id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{t('common.none')}</option>
        {f.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  const ltr = ['email', 'phone', 'country', 'date', 'number'].includes(f.type);
  return (
    <input
      {...props}
      id={id}
      type={f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : f.type === 'email' ? 'email' : f.type === 'phone' ? 'tel' : 'text'}
      {...(ltr ? { dir: 'ltr' } : {})}
      maxLength={f.maxLength ?? 200}
      value={value}
      autoComplete="off"
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

const toText = (v: FieldValue | undefined) => (v === null || v === undefined ? '' : String(v));

/** Person → restricted details (PB-03-07) and configured profile fields (OR-07). */
export function ProfileFieldsPanel({ personId }: { personId: string }) {
  const { t, tk, dateTime } = useI18n();
  const { scopeKey } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: [...scopeKey, 'person-fields', personId], queryFn: () => get<ProfileFieldsResponse>(`/api/v1/people/${personId}/fields`), gcTime: 0 });
  const [editing, setEditing] = useState(false);
  const [revealing, setRevealing] = useState<string[] | null>(null);
  const [revealed, setRevealed] = useState<RevealFieldsResponse | null>(null);
  const [history, setHistory] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // An open preview expires, and is re-validated against current grants every 15 s (PB-03-07).
  useEffect(() => {
    if (!revealed) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const check = setInterval(() => {
      get(`/api/v1/people/${personId}/reveals/${revealed.revealId}`).catch(() => setRevealed(null));
    }, 15_000);
    return () => {
      clearInterval(tick);
      clearInterval(check);
    };
  }, [revealed, personId]);
  useEffect(() => {
    if (revealed && Date.parse(revealed.expiresAt) <= now) setRevealed(null);
  }, [revealed, now]);
  // Leaving the page drops revealed values.
  useEffect(() => () => setRevealed(null), []);

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} />;
  const data = q.data;
  if (data.fields.length === 0 && !data.canViewHistory) return null;
  const secondsLeft = revealed ? Math.max(0, Math.round((Date.parse(revealed.expiresAt) - now) / 1000)) : 0;
  return (
    <section className="panel" aria-labelledby="restricted-title">
      <div className="panel-head">
        <div>
          <h2 id="restricted-title">{t('ppl.fields.title')}</h2>
          <p>{t('ppl.fields.sub')}</p>
        </div>
        <div className="actions">
          {data.fields.some((f) => f.editable) ? (
            <button className="button small" type="button" onClick={() => setEditing(true)}>
              {t('ppl.fields.edit')}
            </button>
          ) : null}
          {data.canViewHistory ? (
            <button className="button small quiet" type="button" onClick={() => setHistory(true)}>
              {t('ppl.fields.history')}
            </button>
          ) : null}
        </div>
      </div>
      <div className="pad">
        {revealed ? (
          <p className="note warning" role="status">
            {t('ppl.fields.revealedFor', { seconds: secondsLeft })}{' '}
            <button className="button small quiet" type="button" onClick={() => setRevealed(null)}>
              {t('ppl.fields.hide')}
            </button>
          </p>
        ) : null}
        {data.fields.length === 0 ? (
          <p className="tiny">{t('ppl.fields.none')}</p>
        ) : (
          <dl className="definition" style={{ gridTemplateColumns: '1fr' }}>
            {data.fields.map((f) => {
              const shown = revealed && f.key in revealed.values ? revealed.values[f.key] : undefined;
              return (
                <div key={f.key}>
                  <dt>
                    {f.label} <span className="badge">{tk(`ppl.class.${f.classification}`)}</span>
                  </dt>
                  <dd>
                    {shown !== undefined ? (
                      <strong dir="auto">{toText(shown)}</strong>
                    ) : f.masked ? (
                      <span data-ltr aria-label={t('ppl.fields.masked')}>
                        {f.maskedValue}
                      </span>
                    ) : f.hasValue ? (
                      <span dir="auto">{toText(f.value)}</span>
                    ) : (
                      t('common.none')
                    )}
                    {f.revealable && shown === undefined ? (
                      <>
                        {' '}
                        <button className="button small quiet" type="button" onClick={() => setRevealing([f.key])}>
                          {t('ppl.fields.reveal')}
                        </button>
                      </>
                    ) : null}
                  </dd>
                </div>
              );
            })}
          </dl>
        )}
        <p className="tiny" style={{ marginBlockStart: 12 }}>
          {t('ppl.fields.maskNote')}
        </p>
      </div>
      {revealing ? (
        <RevealDialog
          personId={personId}
          fields={revealing}
          purposes={data.revealPurposes}
          seconds={data.revealSeconds}
          onClose={() => setRevealing(null)}
          onRevealed={(r) => {
            setRevealed(r);
            setNow(Date.now());
            setRevealing(null);
          }}
        />
      ) : null}
      {editing ? (
        <FieldsEditor
          title={t('ppl.fields.edit')}
          fields={data.fields.filter((f) => f.editable)}
          save={(values) => api<ProfileFieldsResponse>('PUT', `/api/v1/people/${personId}/fields`, { values })}
          onClose={() => setEditing(false)}
          onSaved={(r) => qc.setQueryData([...scopeKey, 'person-fields', personId], r)}
        />
      ) : null}
      {history ? <RevealHistory personId={personId} onClose={() => setHistory(false)} dateTime={dateTime} /> : null}
    </section>
  );
}

function RevealDialog({ personId, fields, purposes, seconds, onClose, onRevealed }: { personId: string; fields: string[]; purposes: ProfileFieldsResponse['revealPurposes']; seconds: number; onClose: () => void; onRevealed: (r: RevealFieldsResponse) => void }) {
  const { t } = useI18n();
  const [purpose, setPurpose] = useState(purposes[0]?.code ?? '');
  const [note, setNote] = useState('');
  const m = useMutation({ mutationFn: () => api<RevealFieldsResponse>('POST', `/api/v1/people/${personId}/fields/reveal`, { fields, purpose, note: note.trim() || null }), onSuccess: onRevealed });
  const fe = m.error instanceof ApiRequestError ? m.error.fields : {};
  return (
    <Dialog open title={t('ppl.reveal.title')} subtitle={t('ppl.reveal.sub', { seconds })} onClose={onClose}>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <ErrorSummary error={m.error} fieldIds={{ purpose: 'reveal-purpose' }} />
        <Field id="reveal-purpose" label={t('ppl.reveal.purpose')} error={fe.purpose} full>
          {(p) => (
            <select {...p} value={purpose} onChange={(e) => setPurpose(e.target.value)}>
              {purposes.map((x) => (
                <option key={x.code} value={x.code}>
                  {x.label}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label={t('ppl.reveal.note')} hint={t('ppl.reveal.noteHint')} error={fe.note} full>
          {(p) => <textarea {...p} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />}
        </Field>
        <p className="tiny">{t('ppl.reveal.audited')}</p>
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="button primary" type="submit" disabled={m.isPending || !purpose}>
            {t('ppl.fields.reveal')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function RevealHistory({ personId, onClose, dateTime }: { personId: string; onClose: () => void; dateTime: (iso: string) => string }) {
  const { t } = useI18n();
  const q = useQuery({ queryKey: ['reveal-history', personId], queryFn: () => get<RevealHistoryResponse>(`/api/v1/people/${personId}/reveals`), gcTime: 0 });
  return (
    <Dialog open title={t('ppl.fields.history')} onClose={onClose}>
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} />
      ) : q.data.items.length === 0 ? (
        <p>{t('ppl.history.none')}</p>
      ) : (
        <div className="tablewrap" tabIndex={0} aria-label={t('ppl.fields.history')}>
          <table>
            <thead>
              <tr>
                <th scope="col">{t('ppl.history.when')}</th>
                <th scope="col">{t('ppl.history.who')}</th>
                <th scope="col">{t('ppl.history.fields')}</th>
                <th scope="col">{t('ppl.reveal.purpose')}</th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((r) => (
                <tr key={r.id}>
                  <td>{dateTime(r.at)}</td>
                  <td>{r.user}</td>
                  <td data-ltr>{r.fields.join(', ')}</td>
                  <td>
                    <code data-ltr>{r.purpose}</code>
                    {r.note ? <small dir="auto">{r.note}</small> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  );
}

/** Edit configured field values; masked values are write-only (typed in full, shown masked). */
export function FieldsEditor({ title, fields, save, onClose, onSaved }: { title: string; fields: readonly ProfileFieldDto[]; save: (values: Record<string, FieldValue>) => Promise<ProfileFieldsResponse>; onClose: () => void; onSaved: (r: ProfileFieldsResponse) => void }) {
  const { t, tk } = useI18n();
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(fields.map((f) => [f.key, f.masked ? '' : toText(f.value)])));
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const m = useMutation({
    mutationFn: () => save(Object.fromEntries([...touched].map((k) => [k, values[k]?.trim() ? values[k]!.trim() : null]))),
    onSuccess: (r) => {
      onSaved(r);
      onClose();
    },
  });
  const fe = m.error instanceof ApiRequestError ? m.error.fields : {};
  return (
    <Dialog open title={title} subtitle={t('ppl.fields.editSub')} onClose={onClose}>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <ErrorSummary error={m.error} fieldIds={Object.fromEntries(fields.map((f) => [`values.${f.key}`, `pf-${f.key}`]))} />
        <div className="formgrid">
          {fields.map((f) => (
            <Field key={f.key} id={`pf-${f.key}`} label={f.label} hint={f.masked ? t('ppl.fields.writeOnly', { masked: f.maskedValue ?? '' }) : (f.help ?? tk(`ppl.class.${f.classification}`))} error={fe[`values.${f.key}`]}>
              {(p) => (
                <FieldInput
                  f={f}
                  id={`pf-${f.key}`}
                  props={p}
                  value={values[f.key] ?? ''}
                  onChange={(v) => {
                    setValues((x) => ({ ...x, [f.key]: v }));
                    setTouched((s) => new Set(s).add(f.key));
                  }}
                />
              )}
            </Field>
          ))}
        </div>
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="button primary" type="submit" disabled={m.isPending || touched.size === 0}>
            {m.isPending ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** Host directory → configured host fields (OR-08); host managers only. */
export function HostFieldsDialog({ hostId, name, onClose }: { hostId: string; name: string; onClose: () => void }) {
  const { t } = useI18n();
  const q = useQuery({ queryKey: ['host-fields', hostId], queryFn: () => get<ProfileFieldsResponse>(`/api/v1/hosts/${hostId}/fields`), gcTime: 0 });
  const qc = useQueryClient();
  if (q.isPending) return null;
  if (q.isError)
    return (
      <Dialog open title={name} onClose={onClose}>
        <ErrorState error={q.error} />
      </Dialog>
    );
  if (q.data.fields.length === 0)
    return (
      <Dialog open title={name} onClose={onClose}>
        <p>{t('ppl.fields.noneHost')}</p>
      </Dialog>
    );
  return <FieldsEditor title={t('ppl.fields.hostTitle', { name })} fields={q.data.fields} save={(values) => api<ProfileFieldsResponse>('PUT', `/api/v1/hosts/${hostId}/fields`, { values })} onClose={onClose} onSaved={(r) => qc.setQueryData(['host-fields', hostId], r)} />;
}

// ------------------------------------------------------------------ visit form (OR-07)
export interface PersonFieldOption {
  readonly key: string;
  readonly label: string;
  readonly help: string | null;
  readonly type: ProfileFieldDto['type'];
  readonly classification: ProfileFieldDto['classification'];
  readonly required: boolean;
  readonly maxLength: number | null;
  readonly options: ProfileFieldDto['options'];
}

/** Profile fields collected for a category at a site (only fields the policy says to collect). */
export function usePersonFields(siteId: string | null | undefined, categoryCode: string | null | undefined) {
  const { scopeKey } = useSession();
  return useQuery({
    queryKey: [...scopeKey, 'person-fields-form', siteId, categoryCode ?? ''],
    enabled: !!siteId,
    queryFn: () => get<{ dictionaryVersion: number; fields: PersonFieldOption[] }>(`/api/v1/field-dictionary/person-fields?siteId=${siteId}${categoryCode ? `&categoryCode=${encodeURIComponent(categoryCode)}` : ''}`),
  }).data?.fields ?? [];
}

export function ParticipantProfileFields({ index, fields, values, errors, onChange }: { index: number; fields: readonly PersonFieldOption[]; values: Readonly<Record<string, string>>; errors: Readonly<Record<string, string>>; onChange: (values: Record<string, string>) => void }) {
  const { t, tk } = useI18n();
  if (fields.length === 0) return null;
  return (
    <div className="formgrid" style={{ marginBlockStart: 8 }}>
      {fields.map((f) => {
        const id = `vf-v${index}-f-${f.key}`;
        return (
          <Field key={f.key} id={id} label={f.required ? `${f.label} · ${t('vform.required')}` : f.label} hint={f.help ?? (f.classification === 'identity' || f.classification === 'restricted' ? tk(`ppl.class.${f.classification}`) : undefined)} error={errors[`participants.${index}.fields.${f.key}`]}>
            {(p) => <FieldInput f={{ type: f.type, options: f.options, maxLength: f.maxLength }} id={id} props={p} value={values[f.key] ?? ''} onChange={(v) => onChange({ ...values, [f.key]: v })} />}
          </Field>
        );
      })}
    </div>
  );
}

/** Non-empty values for the participant payload. */
export function participantFieldValues(values: Readonly<Record<string, string>> | undefined): { fields?: Record<string, string> } {
  const entries = Object.entries(values ?? {}).filter(([, v]) => v.trim() !== '');
  return entries.length ? { fields: Object.fromEntries(entries.map(([k, v]) => [k, v.trim()])) } : {};
}

// ------------------------------------------------------------------ policy editor (PB-03-06)
type ScreeningRule = { mode: 'off' | 'warn' | 'block'; onUnavailable?: 'warn' | 'block' };

export function ScreeningRuleEditor({ value, onChange }: { value: ScreeningRule | undefined; onChange: (v: ScreeningRule | undefined) => void }) {
  const { t } = useI18n();
  const mode = value?.mode ?? 'off';
  return (
    <fieldset className="subsection">
      <legend>{t('ppl.policy.screening')}</legend>
      <p className="tiny">{t('ppl.policy.screeningHint')}</p>
      <div className="formgrid">
        <Field label={t('ppl.policy.mode')}>
          {(p) => (
            <select {...p} value={mode} onChange={(e) => (e.target.value === 'off' ? onChange(undefined) : onChange({ mode: e.target.value as 'warn' | 'block', onUnavailable: value?.onUnavailable ?? 'block' }))}>
              <option value="off">{t('ppl.policy.mode.off')}</option>
              <option value="warn">{t('ppl.policy.mode.warn')}</option>
              <option value="block">{t('ppl.policy.mode.block')}</option>
            </select>
          )}
        </Field>
        {mode !== 'off' ? (
          <Field label={t('ppl.policy.unavailable')}>
            {(p) => (
              <select {...p} value={value?.onUnavailable ?? 'block'} onChange={(e) => onChange({ mode, onUnavailable: e.target.value as 'warn' | 'block' })}>
                <option value="block">{t('ppl.policy.mode.block')}</option>
                <option value="warn">{t('ppl.policy.mode.warn')}</option>
              </select>
            )}
          </Field>
        ) : null}
      </div>
    </fieldset>
  );
}

export function screeningRuleSummary(value: ScreeningRule | undefined, t: ReturnType<typeof useI18n>['t']): string {
  return t(`ppl.policy.mode.${value?.mode ?? 'off'}` as 'ppl.policy.mode.off');
}

// ------------------------------------------------------------------ reception (PB-03-06)
/** Reception may re-run a stale/unavailable check; it learns only the visitor-safe state. */
export function ScreeningCheckButton({ participationId, onDone }: { participationId: string; onDone: () => void }) {
  const { t } = useI18n();
  const m = useMutation({ mutationFn: () => api<ScreeningFactDto>('POST', '/api/v1/screening/checks', { participationId }), onSuccess: onDone });
  return (
    <span className="actions">
      <button className="button small" type="button" disabled={m.isPending} onClick={() => m.mutate()}>
        {m.isPending ? t('common.applying') : t('ppl.rcp.runCheck')}
      </button>
      {m.isSuccess ? <span role="status">{t('ppl.rcp.checkDone')}</span> : null}
      {m.error ? <ErrorSummary error={m.error} fieldIds={{}} /> : null}
    </span>
  );
}
